import { useEffect, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import Plot from 'react-plotly.js'
import { TOKENS, FONT_SANS, FONT_MONO } from './OptimiserDesign'

const PALETTE = ['#4472C4', '#70AD47', '#ED7D31', '#FFC000', '#9E4DD6', '#5DADE2', '#E74C3C', '#26C6DA']
const formatPeriod = (p: number) => `W${String(p).padStart(2, '0')}`
const PLOTLY_CONFIG = { displayModeBar: false, responsive: true }

type Row = Record<string, unknown>
const getStr = (r: Row, k: string) => String(r[k] ?? '')
const getNum = (r: Row, k: string) => Number(r[k] ?? 0)

export const InventoryChart = ({ rows }: { rows: Row[] }) => {
  const [selectedWarehouse, setSelectedWarehouse] = useState<string | null>(null)
  const [showMinMax, setShowMinMax] = useState(false)

  useEffect(() => { setSelectedWarehouse(null) }, [rows])

  const warehouses = useMemo(() => {
    const ws = new Set<string>()
    rows.forEach((r) => ws.add(getStr(r, 'location')))
    return Array.from(ws).sort()
  }, [rows])

  const activeWarehouse = selectedWarehouse ?? warehouses[0] ?? null

  const filtered = useMemo(
    () => rows.filter((r) => getStr(r, 'location') === activeWarehouse),
    [rows, activeWarehouse]
  )

  const periods = useMemo(() => {
    const ps = new Set<number>()
    filtered.forEach((r) => ps.add(getNum(r, 'period')))
    return Array.from(ps).sort((a, b) => a - b)
  }, [filtered])

  const products = useMemo(() => {
    const ps = new Set<string>()
    filtered.forEach((r) => ps.add(getStr(r, 'product')))
    return Array.from(ps).sort()
  }, [filtered])

  const closingMap = useMemo(() => {
    const m: Record<string, Record<number, number>> = {}
    filtered.forEach((r) => {
      const p = getStr(r, 'product')
      const t = getNum(r, 'period')
      m[p] = m[p] ?? {}
      m[p][t] = getNum(r, 'closeInventory')
    })
    return m
  }, [filtered])

  const minByPeriod = useMemo(() => {
    const m: Record<number, number> = {}
    filtered.forEach((r) => { const t = getNum(r, 'period'); m[t] = (m[t] ?? 0) + getNum(r, 'minInventory') })
    return m
  }, [filtered])

  const maxByPeriod = useMemo(() => {
    const m: Record<number, number> = {}
    filtered.forEach((r) => { const t = getNum(r, 'period'); m[t] = (m[t] ?? 0) + getNum(r, 'maxInventory') })
    return m
  }, [filtered])

  const periodLabels = periods.map(formatPeriod)

  const areaTraces = products.map((p, i) => ({
    type: 'scatter' as const, mode: 'lines' as const, name: p, stackgroup: 'inv',
    x: periodLabels, y: periods.map((t) => closingMap[p]?.[t] ?? 0),
    fillcolor: `${PALETTE[i % PALETTE.length]}55`,
    line: { color: PALETTE[i % PALETTE.length], width: 1.5 },
  }))

  const minTrace = showMinMax ? {
    type: 'scatter' as const, mode: 'lines' as const, name: 'Min target',
    x: periodLabels, y: periods.map((t) => minByPeriod[t] ?? 0),
    line: { color: '#E74C3C', dash: 'dot' as const, width: 1.5 },
  } : null

  const maxTrace = showMinMax ? {
    type: 'scatter' as const, mode: 'lines' as const, name: 'Max target',
    x: periodLabels, y: periods.map((t) => maxByPeriod[t] ?? 0),
    line: { color: '#27AE60', dash: 'dot' as const, width: 1.5 },
  } : null

  const traces = [...areaTraces, ...(minTrace ? [minTrace] : []), ...(maxTrace ? [maxTrace] : [])]

  if (rows.length === 0) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontFamily: FONT_SANS, fontSize: 12, color: TOKENS.ink300 }}>
          No inventory data
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${TOKENS.line100}`, flexShrink: 0,
                 display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: FONT_SANS, fontSize: 11.5, fontWeight: 700, color: TOKENS.ink900 }}>
          Inventory Projection
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: FONT_SANS, fontSize: 10, fontWeight: 700, color: TOKENS.ink400,
                            textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            WH
          </Typography>
          {warehouses.map((w) => (
            <Box
              key={w}
              onClick={() => setSelectedWarehouse(w)}
              sx={{
                px: '8px', py: '2px', borderRadius: '100px', fontSize: 10.5, fontFamily: FONT_MONO,
                fontWeight: 600, cursor: 'pointer',
                border: `1.5px solid ${activeWarehouse === w ? TOKENS.brandTeal : TOKENS.line300}`,
                color: activeWarehouse === w ? TOKENS.brandTeal : TOKENS.ink700,
                background: activeWarehouse === w ? TOKENS.blue50 : 'transparent',
                '&:hover': { borderColor: TOKENS.brandTeal },
              }}
            >
              {w}
            </Box>
          ))}
          <Box
            onClick={() => setShowMinMax((v) => !v)}
            sx={{
              px: '8px', py: '2px', borderRadius: '100px', fontSize: 10, fontFamily: FONT_SANS,
              fontWeight: 600, cursor: 'pointer',
              border: `1.5px solid ${showMinMax ? TOKENS.brandTeal : TOKENS.line300}`,
              color: showMinMax ? TOKENS.brandTeal : TOKENS.ink400,
              background: showMinMax ? TOKENS.blue50 : 'transparent',
              '&:hover': { borderColor: TOKENS.brandTeal },
            }}
          >
            Min/Max
          </Box>
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Plot
          data={traces}
          layout={{
            paper_bgcolor: 'transparent', plot_bgcolor: TOKENS.surface50,
            font: { family: FONT_SANS, size: 10, color: TOKENS.ink700 },
            margin: { l: 44, r: 8, t: 8, b: 44 }, autosize: true,
            legend: { orientation: 'h', y: -0.22, x: 0, font: { size: 9 } },
            xaxis: { tickfont: { size: 9, family: FONT_MONO }, gridcolor: TOKENS.line200 },
            yaxis: { tickfont: { size: 9, family: FONT_MONO }, gridcolor: TOKENS.line200,
                     title: { text: 'Stock', font: { size: 9 } } },
          }}
          config={PLOTLY_CONFIG}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler
        />
      </Box>
    </Box>
  )
}
